import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Plug, Filter, ExternalLink, Loader2, MoreVertical, RefreshCw, Link2Off } from 'lucide-react';
import { useIntegrations } from '../hooks/useIntegrations';
import { Integration } from '../services/integrationsService';
import { PageLayout } from '../components/layout';
import { api } from '../services/api';
import { useToast } from '../context/ToastContext';

// Helper function to get status color classes
const getStatusColorClasses = (status: string) => {
  if (!status) return 'bg-gray-100 text-gray-700 border-gray-200';
  switch (status.toLowerCase()) {
    case 'connected':
    case 'active':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'disconnected':
    case 'inactive':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'pending':
      return 'bg-orange-100 text-orange-700 border-orange-200';
    case 'available':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'beta':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'coming_soon':
      return 'bg-purple-100 text-purple-700 border-purple-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};



// Helper function to translate categories
const translateCategory = (category: string, t: any) => {
  if (!category) return '';
  const translated = t(`integrations.categories.${category}`, category.replace(/_/g, ' '));
  return translated.charAt(0).toUpperCase() + translated.slice(1);
};

// Helper function to translate statuses
const translateStatus = (status: string, t: any) => {
  if (!status) return '';
  const translated = t(`integrations.statuses.${status}`, status.replace(/_/g, ' '));
  return translated.charAt(0).toUpperCase() + translated.slice(1);
};









// Function to handle integration configuration
const configureIntegration = async (platform: string, showToast: (message: string, type?: any) => void, setIsConfiguring: (loading: boolean) => void, t: any) => {
  try {
    setIsConfiguring(true);
    const response = await api.get(`/management/integrations/${platform}/config`);

    if (response.data.success && response.data.authUrl) {
      // Redirect to the OAuth URL
      window.location.href = response.data.authUrl;
    } else {
      showToast(t('integrations.configError', 'Failed to configure integration. Please try again.'), 'error');
    }
  } catch (error) {
    console.error('Error configuring integration:', error);
    showToast(t('integrations.configError', 'Failed to configure integration. Please try again.'), 'error');
  } finally {
    setIsConfiguring(false);
  }
};

// Integration disconnect confirmation modal
const DisconnectIntegrationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  integrationName: string;
  integrationLogo?: string;
  isLoading: boolean;
}> = ({ isOpen, onClose, onConfirm, integrationName, integrationLogo, isLoading }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />

        <div className="relative bg-white rounded-xl border border-black shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2 text-red-600">
              <Link2Off className="w-5 h-5" />
              <h2 className="text-xl font-semibold">
                {t('integrations.disconnect.title', 'Disconnect Integration')}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              ×
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-gray-700">
              {t('integrations.disconnect.message', 'Are you sure you want to disconnect {{integrationName}}?', { integrationName })}
            </p>

            <div className="flex items-center gap-3 my-4">
              {integrationLogo ? (
                <img
                  src={integrationLogo}
                  alt={integrationName}
                  className="w-12 h-12 rounded-lg object-contain bg-gray-100"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Plug className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <div>
                <p className="text-gray-700 font-semibold">{integrationName}</p>
                <p className="text-sm text-gray-500">
                  {t('integrations.connected', 'Connected')}
                </p>
              </div>
            </div>

            <p className="text-sm text-red-600">
              {t('integrations.disconnect.warning', 'This action cannot be undone. You will need to reconnect it to use its features.')}
            </p>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
              disabled={isLoading}
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('common.disconnecting', 'Disconnecting...')}
                </>
              ) : (
                t('common.confirm', 'Confirm')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Integration card component
const IntegrationCard: React.FC<{ integration: Integration }> = ({ integration }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [canvaDesigns, setCanvaDesigns] = useState<any[]>([]);
  const [isCanvaDesignsModalOpen, setIsCanvaDesignsModalOpen] = useState(false);
  
  // Safely convert category to array format
  const integrationCategories = React.useMemo(() => {
    if (!integration.category) return [];
    if (Array.isArray(integration.category)) return integration.category;
    if (typeof integration.category === 'string') return [integration.category];
    return [];
  }, [integration.category]);

  // Handle reconnect account
  const handleReconnect = async () => {
    setShowActions(false);
    try {
      setIsConfiguring(true);
      const response = await api.get(`/management/integrations/${integration.name?.toLowerCase() || ''}/config`);

      if (response.data.success && response.data.authUrl) {
        // Store current page in sessionStorage to redirect back here after OAuth
        sessionStorage.setItem('oauth_redirect_after_auth', '/integrations');
        sessionStorage.setItem('oauth_redirect_timestamp', Date.now().toString());
        // Redirect to the OAuth URL
        window.location.href = response.data.authUrl;
      } else {
        showToast(t('integrations.configError', 'Failed to configure integration. Please try again.'), 'error');
      }
    } catch (error) {
      console.error('Error configuring integration:', error);
      showToast(t('integrations.configError', 'Failed to configure integration. Please try again.'), 'error');
    } finally {
      setIsConfiguring(false);
    }
  };

  // Handle disconnect account
  const handleDisconnect = async () => {
    setShowActions(false);
    setIsDisconnectModalOpen(true);
  };

  // Confirm disconnect with API call
  const confirmDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      const response = await api.post(`/management/integrations/${integration.name?.toLowerCase() || ''}/revoke`);
      if (response.data.success) {
        showToast(t('integrations.disconnect.success', 'Integration disconnected successfully'), 'success');
        // Refresh the integrations data
        window.location.reload();
      } else {
        throw new Error(response.data.message || 'Failed to disconnect integration');
      }
    } catch (error) {
      console.error('Error disconnecting integration:', error);
      showToast(t('integrations.disconnect.error', 'Failed to disconnect integration. Please try again.'), 'error');
    } finally {
      setIsDisconnecting(false);
      setIsDisconnectModalOpen(false);
    }
  };
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-6 relative">
      {/* Connect/Connected button in top right - aligned with image top */}
      {(integration.status && (integration.status.toLowerCase() === 'beta' || integration.status.toLowerCase() === 'available')) && (
        <div className="absolute top-6 right-6">
          {/* Check if integration has connected accounts */}
          {integration.accounts && integration.accounts.length > 0 ? (
            <div className="relative">
              <button
                onClick={() => setShowActions(!showActions)}
                className="flex items-center justify-center gap-2 px-3 py-1.5 bg-transparent text-green-600 text-sm font-medium hover:bg-green-50 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" strokeWidth={2} />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 12l3 3L17 9" />
                </svg>
                {t('integrations.connected', 'Connected')}
                <MoreVertical className="w-4 h-4 ml-1" />
              </button>

              {showActions && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowActions(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20">
                    <div className="py-1">
                      <button
                        onClick={handleReconnect}
                        disabled={isConfiguring}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 disabled:opacity-50"
                      >
                        {isConfiguring ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t('integrations.reconnecting', 'Reconnecting...')}
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            {t('integrations.reconnect', 'Reconnect account')}
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleDisconnect}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <Link2Off className="w-4 h-4 mr-2" />
                        {t('integrations.disconnect', 'Disconnect account')}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <button
              onClick={() => configureIntegration(integration.name?.toLowerCase() || '', showToast, setIsConfiguring, t)}
              disabled={isConfiguring}
              className="flex items-center justify-center gap-2 px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isConfiguring ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {t('integrations.configuring', 'Configuring...')}
                </>
              ) : (
                <>
                  <ExternalLink className="w-3 h-3" />
                  {t('integrations.connect', 'Connect')}
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Logo */}
      <div className="flex justify-start mb-4 pr-20">
        <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
          {integration.logoUrl ? (
            <img
              src={integration.logoUrl}
              alt={integration.name}
              className="w-full h-full object-contain"
              onError={(e) => {
                // Fallback to icon if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = '<div class="w-8 h-8 text-gray-400"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg></div>';
                }
              }}
            />
          ) : (
            <Plug className="w-8 h-8 text-gray-400" />
          )}
        </div>
      </div>

      {/* Name */}
      <div className="text-left mb-3">
        <h3 className="text-lg font-semibold text-gray-900">{integration.name}</h3>
      </div>

      {/* Category tags and status */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {integrationCategories.length > 0 && (
          <>
            {integrationCategories.map((cat, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
              >
                {translateCategory(cat, t)}
              </span>
            ))}
          </>
        )}

        {/* Status badge next to categories */}
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColorClasses(integration.status || '')}`}
        >
          {translateStatus(integration.status || '', t)}
        </span>
      </div>

      {/* Description */}
      {integration.description && (
        <div className="text-left mb-4">
          <p className="text-sm text-gray-500 line-clamp-3">{integration.description}</p>
        </div>
      )}

      {/* Disconnect Integration Modal */}
      <DisconnectIntegrationModal
        isOpen={isDisconnectModalOpen}
        onClose={() => setIsDisconnectModalOpen(false)}
        onConfirm={confirmDisconnect}
        integrationName={integration.name || 'this integration'}
        integrationLogo={integration.logoUrl}
        isLoading={isDisconnecting}
      />
    </div>
  );
};

// Integration statuses with descriptions
const integrationStatuses = [
  {
    id: 'available',
    label: 'Available',
    emoji: '🔵',
    description: 'Integration is available for connection. Click to set up and start using it.'
  },
  {
    id: 'beta',
    label: 'Beta',
    emoji: '🧪',
    description: 'This integration is in beta testing phase. Features may be limited or unstable.'
  },
  {
    id: 'coming_soon',
    label: 'Coming Soon',
    emoji: '🚀',
    description: 'This integration is under development and will be available soon.'
  },
  {
    id: 'maintenance',
    label: 'Maintenance',
    emoji: '🔧',
    description: 'Integration is temporarily unavailable due to maintenance. Check back later.'
  },
  {
    id: 'disconnected',
    label: 'Disconnected',
    emoji: '❌',
    description: 'Integration was connected but is now disconnected. Reconnect to restore functionality.'
  },
  {
    id: 'pending',
    label: 'Pending',
    emoji: '⏳',
    description: 'Integration setup is in progress. Please wait while we complete the connection.'
  }
];

// Base category definitions with descriptions (for reference)
const categoryDefinitions = {
  social_media: {
    label: 'Social Media',
    emoji: '📱',
    description: 'Connect your social media accounts to manage posts, analytics, and engagement across platforms.'
  },
  analytics: {
    label: 'Analytics',
    emoji: '📊',
    description: 'Track performance, audience insights, and engagement metrics for your content.'
  },
  email_marketing: {
    label: 'Email Marketing',
    emoji: '📧',
    description: 'Manage email campaigns, subscriber lists, and newsletter automation.'
  },
  crm: {
    label: 'CRM',
    emoji: '👥',
    description: 'Customer relationship management tools to organize contacts and interactions.'
  },
  payment: {
    label: 'Payment',
    emoji: '💳',
    description: 'Payment processors and financial tools for transactions and billing.'
  },
  productivity: {
    label: 'Productivity',
    emoji: '⚡',
    description: 'Tools to streamline workflows, task management, and team collaboration.'
  },
  design: {
    label: 'Design',
    emoji: '🎨',
    description: 'Creative tools for graphic design, content creation, and media editing.'
  },
  communication: {
    label: 'Communication',
    emoji: '💬',
    description: 'Messaging, chat, and communication platforms for team coordination.'
  },
  automation: {
    label: 'Automation',
    emoji: '🤖',
    description: 'Workflow automation, API integrations, and process optimization tools.'
  },
  link_shortener: {
    label: 'Link Shortener',
    emoji: '🔗',
    description: 'URL shortening and link management tools for tracking and analytics.'
  }
};

const Integrations: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [showOnlyConnected, setShowOnlyConnected] = useState(false);

  const { data: integrationsData, isLoading, error } = useIntegrations();
  const integrations = integrationsData?.data?.integrations || [];

  // Handle OAuth redirect back to original page
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const platform = urlParams.get('platform');
    const status = urlParams.get('status');
    const userId = urlParams.get('userId');

    // Check if this is an OAuth redirect (has platform, status, and userId params)
    if (platform && status === 'success' && userId) {
      console.log('🔄 Integrations: Detected OAuth redirect for platform:', platform);

      // Get the stored redirect path
      const redirectPath = sessionStorage.getItem('oauth_redirect_after_auth');

      if (redirectPath && redirectPath !== '/integrations') {
        console.log('🔄 Integrations: Redirecting to stored path:', redirectPath);

        // Clear the stored path
        sessionStorage.removeItem('oauth_redirect_after_auth');
        sessionStorage.removeItem('oauth_redirect_timestamp');

        // Redirect to the original page
        navigate(redirectPath, { replace: true });
        return;
      }
    }
  }, [location.search, navigate]);

  useEffect(() => {
    document.title = t('integrations.title', 'Integrations');
  }, [t]);

  // Get available categories from integrations data
  const availableCategories = React.useMemo(() => {
    const categorySet = new Set<string>();
    integrations.forEach(integration => {
      if (integration.category) {
        if (Array.isArray(integration.category)) {
          integration.category.forEach(cat => categorySet.add(cat));
        } else if (typeof integration.category === 'string') {
          categorySet.add(integration.category);
        }
      }
    });
    return Array.from(categorySet).sort();
  }, [integrations]);

  // Get categories with descriptions for display
  const categoriesWithDetails = React.useMemo(() => {
    return availableCategories.map(categoryId => {
      const definition = categoryDefinitions[categoryId as keyof typeof categoryDefinitions];
      if (definition) {
        return {
          id: categoryId,
          label: t(`integrations.categories.${categoryId}`, definition.label),
          emoji: definition.emoji,
          description: t(`integrations.categories.${categoryId}Desc`, definition.description)
        };
      }
      // Fallback for unknown categories
      const formattedLabel = categoryId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      return {
        id: categoryId,
        label: t(`integrations.categories.${categoryId}`, formattedLabel),
        emoji: '📁',
        description: t(`integrations.categories.${categoryId}Desc`, `${formattedLabel} integration tools and services`)
      };
    });
  }, [availableCategories, t]);

    // Filter integrations
  const filteredIntegrations = React.useMemo(() => {
    return integrations.filter(integration => {
      const matchesSearch = !searchQuery ||
        (integration.name && integration.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (integration.description && integration.description.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesStatus = selectedStatuses.length === 0 || (integration.status && selectedStatuses.includes(integration.status));

      // Handle category filtering for different data types
      const matchesCategory = selectedCategories.length === 0 || (() => {
        if (!integration.category) return false;
        if (Array.isArray(integration.category)) {
          return integration.category.some(cat => selectedCategories.includes(cat));
        }
        if (typeof integration.category === 'string') {
          return selectedCategories.includes(integration.category);
        }
        return false;
      })();

      // Handle connected accounts filter
      const matchesConnected = !showOnlyConnected || (integration.accounts && integration.accounts.length > 0);

      return matchesSearch && matchesStatus && matchesCategory && matchesConnected;
    });
  }, [integrations, searchQuery, selectedStatuses, selectedCategories, showOnlyConnected]);

  if (isLoading) {
    // Skeleton loader
    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-4 lg:pt-0">
        <div className="w-[95%] mx-auto py-8">
          {/* Header skeleton */}
          <div className="mb-8">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
          </div>
          
          {/* Filters skeleton */}
          <div className="mb-6 flex flex-col md:flex-row gap-4">
            <div className="h-10 bg-gray-200 rounded-lg flex-1 animate-pulse"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-48 animate-pulse"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-48 animate-pulse"></div>
          </div>
          
          {/* Cards skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                  </div>
                </div>
                <div className="flex gap-2 mb-4">
                  <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                  <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                </div>
                <div className="flex justify-between">
                  <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                  <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-4 lg:pt-0">
        <div className="w-[95%] mx-auto py-8 flex items-center justify-center h-96">
          <div className="text-center">
            <p className="text-red-600 mb-4">Failed to load integrations</p>
            <p className="text-gray-500">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageLayout
        title={t('integrations.title', 'Integrations')}
        subtitle={t('integrations.subtitle', 'Connect your favorite tools and services')}
      >

        {/* Filters */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={t('integrations.searchPlaceholder', 'Search integrations...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Filters button */}
          {(() => {
            const activeFiltersCount = selectedStatuses.length + selectedCategories.length + (showOnlyConnected ? 1 : 0);
            return (
              <div className="relative">
                <button
                  onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:text-primary-600"
                  aria-expanded={isFiltersOpen}
                >
                  <Filter className="w-4 h-4 mr-2 text-primary-500" />
                  {t('integrations.filters', 'Filtres')}
                  {activeFiltersCount > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center rounded-full bg-primary-600 text-white text-xs w-5 h-5">
                      {activeFiltersCount}
                    </span>
                  )}
                </button>
              </div>
            );
          })()}
        </div>

        {/* Results count */}
        <div className="mb-6">
          <p className="text-sm text-gray-500">
            {t('integrations.resultsCount', {
              count: filteredIntegrations.length,
              total: integrations.length,
              defaultValue: 'Showing {{count}} of {{total}} integrations'
            })}
          </p>
        </div>

        {/* Integrations grid */}
        {filteredIntegrations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6">
            {filteredIntegrations.map((integration) => (
              <IntegrationCard key={integration.id} integration={integration} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Plug className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('integrations.noResults', 'No integrations found')}
            </h3>
            <p className="text-gray-500">
              {searchQuery || selectedStatuses.length > 0 || selectedCategories.length > 0
                ? t('integrations.noResultsFiltered', 'Try adjusting your search or filter criteria')
                : t('integrations.noIntegrations', 'No integrations are available at the moment')
              }
            </p>
          </div>
        )}
    </PageLayout>

    {/* Right-side sliding Filters drawer */}
    {isFiltersOpen && (
      <div className="fixed inset-0 z-50">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/30"
          onClick={() => setIsFiltersOpen(false)}
        />
        {/* Panel */}
        <div className="absolute right-0 top-0 h-full w-full sm:w-[380px] bg-white shadow-2xl border-l border-gray-200 transform transition-transform duration-300 ease-out">
          <div className="h-full flex flex-col">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{t('integrations.filters', 'Filtres')}</h3>
              <button
                onClick={() => setIsFiltersOpen(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
              {/* Connected accounts filter */}
              <div>
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={() => setShowOnlyConnected(!showOnlyConnected)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 mt-1 ${
                      showOnlyConnected ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        showOnlyConnected ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-gray-900 flex items-center gap-2">
                      <span className="shrink-0">✅</span>
                      <span className="font-semibold">{t('integrations.showOnlyConnected', 'Show only connected')}</span>
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                      {t('integrations.showOnlyConnectedDesc', 'Only show integrations that have connected accounts')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Status group */}
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-3">{t('integrations.filterByStatus', 'Filter by Status')}</div>
                <div className="space-y-3">
                  {integrationStatuses.map((status) => (
                    <div key={status.id} className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1 rounded border-gray-300 text-primary-600"
                        checked={selectedStatuses.includes(status.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStatuses([...selectedStatuses, status.id]);
                          } else {
                            setSelectedStatuses(selectedStatuses.filter(s => s !== status.id));
                          }
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-gray-900 flex items-center gap-2">
                          <span className="shrink-0">{status.emoji}</span>
                          <span className="truncate font-semibold">{t(`integrations.statuses.${status.id}`, status.label)}</span>
                        </div>
                        <div className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                          {t(`integrations.statuses.${status.id}Desc`, status.description)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Categories group */}
              <div>
                <div className="text-sm font-semibold text-gray-900 mb-3">{t('integrations.filterByCategory', 'Filter by Category')}</div>
                <div className="space-y-3">
                  {categoriesWithDetails.map((category) => (
                    <div key={category.id} className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1 rounded border-gray-300 text-primary-600"
                        checked={selectedCategories.includes(category.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCategories([...selectedCategories, category.id]);
                          } else {
                            setSelectedCategories(selectedCategories.filter(c => c !== category.id));
                          }
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-gray-900 flex items-center gap-2">
                          <span className="shrink-0">{category.emoji}</span>
                          <span className="truncate font-semibold">{category.label}</span>
                        </div>
                        <div className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                          {category.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
                          <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedStatuses([]);
                    setSelectedCategories([]);
                    setShowOnlyConnected(false);
                  }}
                  className="px-4 py-2 text-sm rounded-lg text-gray-700 hover:text-primary-600"
                >
                  {t('integrations.clearFilters', 'Clear all filters')}
                </button>
                <div />
              </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Integrations; 